import { Avatar, Badge, Button, Group, Menu, Text, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import { devBypass, signOut } from '../auth/msal';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { type PortalLocale, portalLocale } from '../lib/locale';
import { brand } from '../theme/brand';
import { appLayoutCopy } from './appLayoutCopy';

/** Signed-in identity + sign-out, sitting on the deep-teal header band. */
export function UserMenu({ locale = portalLocale() }: { locale?: PortalLocale }) {
  const { name, username, initials, roles, viaGlobalAdmin } = useCurrentUser();
  const [opened, setOpened] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const text = appLayoutCopy(locale);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch (err) {
      setSigningOut(false);
      console.error('[signout]', err);
    }
  };

  return (
    <Menu shadow="md" width={264} position="bottom-end" withinPortal opened={opened} onChange={setOpened}>
      <Menu.Target>
        <UnstyledButton
          aria-label={text.account(name)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 8px',
            borderRadius: 8,
            color: brand.sand,
          }}
        >
          <Avatar
            radius="xl"
            size={30}
            styles={{
              root: { backgroundColor: brand.sea, border: '1px solid rgba(255,255,255,0.25)' },
              // `backgroundColor` has to be said here as well as on the root. The initials live in
              // Mantine's placeholder element, which paints its own background — near-white under a
              // light colour scheme — and cream letters on that are a blank circle. The header band
              // is deep teal in both schemes, so the placeholder must let the root's teal through
              // rather than take the scheme's grey.
              placeholder: {
                backgroundColor: 'transparent',
                color: brand.sand,
                fontWeight: 700,
                fontSize: 12,
              },
            }}
          >
            {initials}
          </Avatar>
          <Text size="sm" fw={600} visibleFrom="sm" style={{ color: brand.sand }}>
            {name}
          </Text>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <div style={{ padding: '10px 12px 8px' }}>
          <Text size="sm" fw={700} lh={1.2}>
            {name}
          </Text>
          {username ? (
            <Text size="xs" c="dimmed" lh={1.3} style={{ wordBreak: 'break-all' }}>
              {username}
            </Text>
          ) : null}
          {devBypass ? (
            <Badge size="xs" color="orange" variant="light" mt={8}>
              {text.devBypass}
            </Badge>
          ) : (
            <Group gap={4} mt={8}>
              {viaGlobalAdmin ? (
                <Badge size="xs" variant="light" color="grape">
                  {text.globalAdministrator}
                </Badge>
              ) : null}
              {roles.map((role) => (
                <Badge key={role} size="xs" variant="light">
                  {role}
                </Badge>
              ))}
            </Group>
          )}
        </div>

        <Menu.Divider />

        {devBypass ? (
          <Menu.Item disabled>{text.signOutUnavailable}</Menu.Item>
        ) : (
          <Menu.Item component="div" closeMenuOnClick={false} p={8}>
            <Button
              fullWidth
              variant="light"
              color="red"
              loading={signingOut}
              onClick={() => void handleSignOut()}
            >
              {text.signOut}
            </Button>
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
